import { getDatabase, ref, get } from 'firebase/database';
import { app } from '../firebase';
import { getCachedTemas, getCachedPreguntasPorTema } from '../services/rtdbCache';
import { getTemaIdsParaModulos, MODULO_A_TEMA_IDS } from '../constants/universitiesData';

const CENEVAL_TEMA_IDS = new Set(
  getTemaIdsParaModulos(Object.keys(MODULO_A_TEMA_IDS))
);

// Reactivos de muestra oficial EXANI-II (Ceneval) con formato de 3 opciones (A, B, C)
export const DEFAULT_EXANI_QUESTIONS = [
  {
    id: 2001,
    tema_id: 201,
    materia: 'Comprensión Lectora',
    pregunta: 'Identifica la idea principal del siguiente fragmento:\n"La transición hacia energías renovables no solo reduce la emisión de gases de efecto invernadero, sino que también estimula la innovación tecnológica y diversifica las matrices energéticas nacionales frente a la volatilidad de los combustibles fósiles."',
    explicacion: 'La idea principal engloba los múltiples beneficios estratégicos y ecológicos que aporta la adopción de energías renovables frente a los hidrocarburos.',
    respuestas: [
      { id: 0, respuesta: 'La adopción de energías limpias ofrece ventajas tanto ambientales como de desarrollo tecnológico y estabilidad energética.', correcta: true },
      { id: 1, respuesta: 'Los combustibles fósiles son la causa única del encarecimiento de la tecnología mundial.', correcta: false },
      { id: 2, respuesta: 'La reducción de emisiones es el único beneficio demostrable de las nuevas matrices energéticas.', correcta: false }
    ]
  },
  {
    id: 2002,
    tema_id: 202,
    materia: 'Redacción Indirecta',
    pregunta: 'Selecciona el enunciado que presenta una concordancia gramatical y ortografía correctas para un texto académico:',
    explicacion: 'El sujeto plural compuesto ("El análisis y la interpretación") concuerda en plural con el verbo ("requirieron"), y las palabras esdrújulas y agudas llevan tilde normativa.',
    respuestas: [
      { id: 0, respuesta: 'El análisis y la interpretación de las muestras requirieron una metodología rigurosa.', correcta: true },
      { id: 1, respuesta: 'El analisis y la interpretación de las muestras requirió una metodologia rigurosa.', correcta: false },
      { id: 2, respuesta: 'El análisis y la interpretación de las muestras requerian una metodolojia rigurosa.', correcta: false }
    ]
  },
  {
    id: 2003,
    tema_id: 203,
    materia: 'Pensamiento Matemático',
    pregunta: 'En un grupo escolar de 40 aspirantes, 24 son mujeres y 16 son hombres. Si se selecciona un aspirante al azar, ¿cuál es la probabilidad simplificada de elegir a un hombre?',
    explicacion: 'Probabilidad P = casos favorables / casos totales = 16 / 40 = 2 / 5 = 40%.',
    respuestas: [
      { id: 0, respuesta: '2/5', correcta: true },
      { id: 1, respuesta: '3/5', correcta: false },
      { id: 2, respuesta: '1/4', correcta: false }
    ]
  },
  {
    id: 2004,
    tema_id: 203,
    materia: 'Pensamiento Matemático',
    pregunta: '¿Cuál es la solución para x en la ecuación lineal: 3(x - 4) = 2x + 5?',
    explicacion: '3x - 12 = 2x + 5 => 3x - 2x = 5 + 12 => x = 17.',
    respuestas: [
      { id: 0, respuesta: 'x = 17', correcta: true },
      { id: 1, respuesta: 'x = 7', correcta: false },
      { id: 2, respuesta: 'x = -1', correcta: false }
    ]
  },
  {
    id: 2005,
    tema_id: 204,
    materia: 'Inglés Diagnóstico (B1)',
    pregunta: 'Complete the sentence with the correct verb form:\n"If the research team _______ the experiment before Friday, they will publish the findings next month."',
    explicacion: 'Primer condicional (First Conditional): If + present simple (finishes), will + verb base.',
    respuestas: [
      { id: 0, respuesta: 'finishes', correcta: true },
      { id: 1, respuesta: 'finished', correcta: false },
      { id: 2, respuesta: 'will finish', correcta: false }
    ]
  },
  {
    id: 2006,
    tema_id: 205,
    materia: 'Ciencias de la Salud / Premedicina',
    pregunta: '¿Qué órgano del sistema circulatorio humano es el encargado principal de impulsar la sangre desoxigenada hacia la circulación pulmonar?',
    explicacion: 'El ventrículo derecho bombea sangre desoxigenada a través de la arteria pulmonar hacia los pulmones para su hematosis.',
    respuestas: [
      { id: 0, respuesta: 'Ventrículo derecho', correcta: true },
      { id: 1, respuesta: 'Aurícula izquierda', correcta: false },
      { id: 2, respuesta: 'Ventrículo izquierdo', correcta: false }
    ]
  },
  {
    id: 2007,
    tema_id: 206,
    materia: 'Cálculo Diferencial e Integral',
    pregunta: '¿Cuál es la derivada de la función f(x) = 4x³ - 5x² + 7x - 9 con respecto a x?',
    explicacion: 'Aplicando la regla de potencias: d/dx(4x³) = 12x², d/dx(-5x²) = -10x, d/dx(7x) = 7, d/dx(-9) = 0. f\'(x) = 12x² - 10x + 7.',
    respuestas: [
      { id: 0, respuesta: 'f\'(x) = 12x² - 10x + 7', correcta: true },
      { id: 1, respuesta: 'f\'(x) = 12x³ - 10x² + 7', correcta: false },
      { id: 2, respuesta: 'f\'(x) = 4x² - 5x + 7', correcta: false }
    ]
  },
  {
    id: 2008,
    tema_id: 207,
    materia: 'Física',
    pregunta: 'Un móvil parte del reposo y acelera uniformemente a razón de 3 m/s² durante 6 segundos. ¿Qué distancia recorre en ese intervalo?',
    explicacion: 'Fórmula d = v₀t + 1/2 a t². Dado que v₀ = 0: d = 1/2 (3 m/s²)(6 s)² = 1/2 (3)(36) = 54 metros.',
    respuestas: [
      { id: 0, respuesta: '54 metros', correcta: true },
      { id: 1, respuesta: '18 metros', correcta: false },
      { id: 2, respuesta: '108 metros', correcta: false }
    ]
  }
];

// Alias para compatibilidad regresiva
export const DEFAULT_MILITARY_QUESTIONS = DEFAULT_EXANI_QUESTIONS;

// Normalizar estructura de preguntas para manejo seguro
export function normalizeQuestion(q) {
  if (!q) return q;
  const preguntaText = q.pregunta || q.question || '';
  const explicacionText = q.explicacion || q.explanation || '';
  let respuestasArray = [];

  if (Array.isArray(q.respuestas)) {
    respuestasArray = q.respuestas.filter(r => r !== null && r !== undefined).map((r, idx) => {
      if (typeof r === 'string') {
        return { id: idx, respuesta: r, correcta: false };
      }
      return {
        id: r?.id !== undefined ? r.id : idx,
        respuesta: r?.respuesta || r?.text || '',
        correcta: r?.correcta === true || r?.correcta === 1 || String(r?.correcta) === "1" || String(r?.correcta).toLowerCase() === "true"
      };
    });
  } else if (q.respuestas && typeof q.respuestas === 'object') {
    respuestasArray = Object.values(q.respuestas).filter(r => r !== null && r !== undefined).map((r, idx) => {
      if (typeof r === 'string') {
        return { id: idx, respuesta: r, correcta: false };
      }
      return {
        id: r?.id !== undefined ? r.id : idx,
        respuesta: r?.respuesta || r?.text || '',
        correcta: r?.correcta === true || r?.correcta === 1 || String(r?.correcta) === "1" || String(r?.correcta).toLowerCase() === "true"
      };
    });
  } else if (Array.isArray(q.opciones || q.options)) {
    const options = q.opciones || q.options;
    const correctIdx = q.correctIndex !== undefined ? q.correctIndex : (q.correctAnswer !== undefined ? q.correctAnswer : 0);
    respuestasArray = options.map((opt, idx) => ({
      id: idx,
      respuesta: typeof opt === 'string' ? opt : (opt?.respuesta || opt?.text || ''),
      correcta: idx === parseInt(correctIdx)
    }));
  }

  return {
    ...q,
    pregunta: preguntaText,
    explicacion: explicacionText,
    respuestas: respuestasArray
  };
}

// Extraer texto de la respuesta correcta de forma 100% segura frente a valores nulos o tipos inconsistentes
export function getCorrectAnswerText(card) {
  if (!card) return 'Sin respuesta disponible.';
  
  if (Array.isArray(card.respuestas)) {
    const valid = card.respuestas.filter(r => r !== null && r !== undefined);
    const correct = valid.find(r => typeof r === 'object' && (r?.correcta === true || r?.correcta === 1 || String(r?.correcta) === "1" || String(r?.correcta).toLowerCase() === "true"));
    if (correct) return correct.respuesta || correct.text || '';
    const first = valid[0];
    if (typeof first === 'object' && first !== null) return first.respuesta || first.text || '';
    if (typeof first === 'string') return first;
  } else if (typeof card.respuestas === 'object' && card.respuestas !== null) {
    const entries = Object.values(card.respuestas).filter(r => r !== null && r !== undefined);
    const correct = entries.find(r => typeof r === 'object' && (r?.correcta === true || r?.correcta === 1 || String(r?.correcta) === "1" || String(r?.correcta).toLowerCase() === "true"));
    if (correct) return correct.respuesta || correct.text || '';
    const first = entries[0];
    if (typeof first === 'object' && first !== null) return first.respuesta || first.text || '';
    if (typeof first === 'string') return first;
  }

  return 'Respuesta registrada en la clave oficial.';
}

// Servicio unificado para cargar preguntas y materias
// Si se pasa userProfile, filtra por los módulos de la carrera del usuario
export async function loadStudyQuestions(userProfile = null) {
  let allQuestions = [];
  let materiasMap = {};

  // Obtener tema_ids permitidos según los módulos del usuario
  let allowedTemaIds = null;
  if (userProfile?.modulosRequeridos && userProfile.modulosRequeridos.length > 0) {
    allowedTemaIds = new Set(getTemaIdsParaModulos(userProfile.modulosRequeridos));
    console.log('[studyData] 🎯 Filtrando por módulos del usuario:', userProfile.modulosRequeridos, '->', allowedTemaIds.size, 'tema_ids permitidos');
  }

  try {
    const [cachedTemas, cachedPreguntas] = await Promise.all([
      getCachedTemas(),
      getCachedPreguntasPorTema()
    ]);

    if (cachedTemas.length > 0 && cachedPreguntas.length > 0) {
      cachedTemas.forEach(t => {
        if (t && t.id) materiasMap[t.id] = t.nombre || `Materia #${t.id}`;
      });

      allQuestions = cachedPreguntas
        .filter(q => {
          // Un perfil activo debe quedar limitado a módulos CENEVAL explícitos.
          if (allowedTemaIds) {
            return q.tema_id !== undefined && q.tema_id !== null && allowedTemaIds.has(Number(q.tema_id));
          }
          return true;
        })
        .map(q => {
          const matName = materiasMap[q.tema_id] || q.subarea || q.materia || 'Módulo General EXANI-II';
          return normalizeQuestion({
            ...q,
            materia: matName
          });
        });
      console.log(`[studyData] 📦 Preguntas cargadas desde caché: ${allQuestions.length} (filtradas: ${allowedTemaIds ? 'sí' : 'no'})`);
    } else {
      const rtdb = getDatabase(app);

      const temasSnap = await get(ref(rtdb, 'temas'));
      if (temasSnap.exists()) {
        const tData = temasSnap.val();
        Object.entries(tData).forEach(([id, t]) => {
          materiasMap[id] = t.nombre || `Tema #${id}`;
        });
      }

      const preguntasSnap = await get(ref(rtdb, 'preguntas_por_tema'));
      if (preguntasSnap.exists()) {
        const data = preguntasSnap.val();
        for (const temaId of Object.keys(data)) {
          // Filtrar por tema_id permitido
          if (allowedTemaIds && !allowedTemaIds.has(Number(temaId))) {
            continue;
          }
          const matName = materiasMap[temaId] || `Materia #${temaId}`;
          for (const qKey of Object.keys(data[temaId])) {
            const q = data[temaId][qKey];
            allQuestions.push(normalizeQuestion({
              id: qKey,
              ...q,
              tema_id: temaId,
              materia: matName
            }));
          }
        }
      }
    }
  } catch (err) {
    console.warn('Uso de banco de preguntas alternativo/local:', err);
  }

  if (allQuestions.length < 5) {
    // También filtrar el fallback si hay filtro de módulos
    let fallbackQuestions = DEFAULT_MILITARY_QUESTIONS.map(normalizeQuestion);
    if (allowedTemaIds) {
      fallbackQuestions = fallbackQuestions.filter(q => 
        q.tema_id !== undefined && q.tema_id !== null && allowedTemaIds.has(Number(q.tema_id))
      );
    }
    allQuestions = [...allQuestions, ...fallbackQuestions];
  }

  return allQuestions;
}

// Cargar y guardar progreso de Flashcards
export function getFlashcardProgress() {
  try {
    const saved = localStorage.getItem('vortex_flashcard_progress');
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    return {};
  }
}

export function saveFlashcardProgress(cardId, level) {
  // level: 'EASY' (Verde), 'MEDIUM' (Amarillo), 'HARD' (Rojo)
  try {
    const current = getFlashcardProgress();
    current[cardId] = {
      level,
      timestamp: Date.now(),
    };
    localStorage.setItem('vortex_flashcard_progress', JSON.stringify(current));
  } catch (e) {
    console.error('Error guardando progreso de flashcard:', e);
  }
}

// Cargar e integrar mapa de calor de debilidades
export function getWeaknessHeatmap(allQuestions = []) {
  const flashcardProgress = getFlashcardProgress();
  const historyRaw = localStorage.getItem('vortex_quiz_history') || localStorage.getItem('quiz_results_history');
  let quizHistory = [];
  try {
    if (historyRaw) quizHistory = JSON.parse(historyRaw);
  } catch (e) {}

  // Agrupar preguntas por materia
  const materiaMap = {};

  allQuestions.forEach(q => {
    const mat = q.materia || 'Legislación General';
    if (!materiaMap[mat]) {
      materiaMap[mat] = {
        materia: mat,
        totalQuestions: 0,
        easyCount: 0,
        mediumCount: 0,
        hardCount: 0,
        quizCorrect: 0,
        quizTotal: 0,
        questions: []
      };
    }

    materiaMap[mat].totalQuestions++;
    materiaMap[mat].questions.push(q);

    // Revisar progreso de flashcard
    const cardStatus = flashcardProgress[q.id];
    if (cardStatus) {
      if (cardStatus.level === 'EASY') materiaMap[mat].easyCount++;
      else if (cardStatus.level === 'MEDIUM') materiaMap[mat].mediumCount++;
      else if (cardStatus.level === 'HARD') materiaMap[mat].hardCount++;
    }
  });

  // Procesar historial de quizzes si existen
  if (Array.isArray(quizHistory)) {
    quizHistory.forEach(quiz => {
      const mat = quiz.materia || quiz.quizType || 'Legislación General';
      if (materiaMap[mat]) {
        materiaMap[mat].quizCorrect += (quiz.score || 0);
        materiaMap[mat].quizTotal += (quiz.totalQuestions || 0);
      }
    });
  }

  // Calcular porcentaje de dominio y color de semáforo
  return Object.values(materiaMap).map(m => {
    const evaluated = m.easyCount + m.mediumCount + m.hardCount + m.quizTotal;
    
    let percentage = 0;
    if (evaluated > 0) {
      const scoreFromCards = (m.easyCount * 1.0) + (m.mediumCount * 0.5);
      const totalPointsPossible = (m.easyCount + m.mediumCount + m.hardCount) + m.quizTotal;
      const totalPointsEarned = scoreFromCards + m.quizCorrect;
      percentage = Math.round((totalPointsEarned / (totalPointsPossible || 1)) * 100);
    } else {
      // Sin practicar aún
      percentage = 0;
    }

    // Código de semáforo
    let status = 'RED'; // 🔴 < 60%
    let statusText = 'Atención Urgente';
    let badgeColor = '#ff4d4d';

    if (percentage >= 85) {
      status = 'GREEN'; // 🟢 >= 85%
      statusText = 'Dominado';
      badgeColor = '#10b981';
    } else if (percentage >= 60) {
      status = 'YELLOW'; // 🟡 60-84%
      statusText = 'En Progreso';
      badgeColor = '#f59e0b';
    } else if (evaluated === 0) {
      status = 'RED';
      statusText = 'Por Iniciar';
      badgeColor = '#ef4444';
    }

    return {
      ...m,
      percentage,
      status,
      statusText,
      badgeColor,
      evaluated
    };
  }).sort((a, b) => a.percentage - b.percentage); // Ordenar por menor porcentaje primero (debilidades principales)
}

// ─────────────────────────────────────────────────────────────
// Modo Guardia: cargador de preguntas CONSCIENTE DEL PERFIL
// Consulta los 'conocimientos específicos' (Área 3) del usuario
// según su grado/jerarquía y fusiona sus quizzes personales.
// ─────────────────────────────────────────────────────────────

// Normaliza una cadena para comparar jerarquías (misma lógica que SimuladorExamen)
function normalizeJerarquiaKey(str = '') {
  return str
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[_\s]+DE[_\s]+/g, '_')
    .trim()
    .replace(/[_\s]+/g, '_');
}

// Mapea el texto del grado del usuario a la clave de jerarquía del simulador
export function mapGradoToJerarquiaKey(grado) {
  const g = (grado || '').toLowerCase().replace(/_/g, ' ').replace(/\./g, ' ');

  if (g.includes('marinero') || g.includes('mro')) return 'marinero';
  if (g.includes('cabo') || g.includes('cbo')) return 'cabo';
  if (g.includes('tercer maestre') || g.includes('3rm') || g.includes('3er')) return 'tercer_maestre';
  if (g.includes('segundo maestre') || g.includes('2dm') || g.includes('2do')) return 'segundo_maestre';
  if (g.includes('primer maestre') || g.includes('1rm') || g.includes('1er')) return 'primer_maestre';
  if (g.includes('teniente corbeta') || g.includes('teniente de corbeta') || g.includes('tte corb') || g.includes('ttc')) return 'teniente_corbeta';
  if (g.includes('teniente fragata') || g.includes('teniente de fragata') || g.includes('tte frag') || g.includes('ttf')) return 'teniente_fragata';
  if (g.includes('teniente navío') || g.includes('teniente navio') || g.includes('teniente de navío') || g.includes('tte nav') || g.includes('ttn')) return 'teniente_navio';
  if (g.includes('capitán corbeta') || g.includes('capitan corbeta') || g.includes('capitán de corbeta') || g.includes('cap corb') || g.includes('ctc')) return 'capitan_corbeta';
  return null;
}

const GUARDIA_JERARQUIA_SEARCH = {
  marinero: 'MRO',
  cabo: 'CBO',
  tercer_maestre: '3RM',
  segundo_maestre: '2DM',
  primer_maestre: '1RM',
  teniente_corbeta: 'TTE.CORB.',
  teniente_fragata: 'TTE.FRAG.',
  teniente_navio: 'TTE.NAV.',
  capitan_corbeta: 'CAP.CORB.'
};

const GUARDIA_JERARQUIA_NOMBRE = {
  marinero: 'Marinero',
  cabo: 'Cabo',
  tercer_maestre: 'Tercer Maestre',
  segundo_maestre: 'Segundo Maestre',
  primer_maestre: 'Primer Maestre',
  teniente_corbeta: 'Teniente de Corbeta',
  teniente_fragata: 'Teniente de Fragata',
  teniente_navio: 'Teniente de Navío',
  capitan_corbeta: 'Capitán de Corbeta'
};

const HIERARCHY_TO_RTDB = {
  'marinero': 'MARINERO',
  'cabo': 'CABO',
  'tercer_maestre': 'TERCER_MAESTRE',
  'segundo_maestre': 'SEGUNDO_MAESTRE',
  'primer_maestre': 'PRIMER_MAESTRE',
  'suboficial': 'SUBOFICIAL',
  'teniente_corbeta': 'TENIENTE_CORBETA',
  'teniente_fragata': 'TENIENTE_FRAGATA',
  'teniente_navio': 'TENIENTE_NAVIO',
  'capitan_corbeta': 'CAPITAN_CORBETA'
};

// Carga datos específicos del Modo Guardia:
//  - preguntas base CENEVAL filtradas por módulos y grado del usuario
//  - filtra por módulos de universidad/carrera seleccionada
export async function loadGuardiaQuestions(profileData, _uid) {
  const rtdb = getDatabase(app);

  const grado = profileData?.grado || profileData?.jerarquia || profileData?.hierarchy || '';
  const jerarquiaKey = mapGradoToJerarquiaKey(grado);

  let temas = [];
  try {
    const cachedTemas = await getCachedTemas();
    if (cachedTemas.length > 0) {
      temas = cachedTemas;
      console.log('[Modo Guardia] 📦 Temas cargados desde caché');
    } else {
      const temasSnap = await get(ref(rtdb, 'temas'));
      if (temasSnap.exists()) {
        temas = Object.entries(temasSnap.val()).map(([id, data]) => ({ id, ...data }));
      }
    }
  } catch (err) {
    console.warn('[Modo Guardia] No se pudieron cargar los temas:', err);
  }

  const temaCoincideConGrado = (tema) => {
    if (!jerarquiaKey) return true; // sin grado configurado → sin filtro
    if (!tema || !tema.grados) return true; // tema visible para todos
    const searchRank = GUARDIA_JERARQUIA_SEARCH[jerarquiaKey];
    const nombreNorm = normalizeJerarquiaKey(GUARDIA_JERARQUIA_NOMBRE[jerarquiaKey]);
    const searchNorm = normalizeJerarquiaKey(searchRank);
    const tokens = String(tema.grados).split(',').map(t => t.trim());
    return tokens.some(t => {
      const up = t.toUpperCase();
      if (up === 'TODOS') return true;
      if (up === searchRank.toUpperCase()) return true;
      if (up === searchRank.replace(/_/g, ' ')) return true;
      const norm = normalizeJerarquiaKey(t);
      return norm === searchNorm || norm === nombreNorm || norm === normalizeJerarquiaKey(jerarquiaKey.replace(/_/g, ' '));
    });
  };

  // 3. Cargar las preguntas base (banco completo) - PASAR profileData para filtrar por módulos carrera
  const preguntasBase = await loadStudyQuestions(profileData);

  // 4. Mantener solo preguntas base asociadas a temas oficiales CENEVAL.
  //    Las especialidades y quizzes personales se cargan aparte y no forman
  //    parte del conjunto reproducible de Modo Audio.
  let filtradas = preguntasBase.filter(q =>
    q.tema_id !== undefined && q.tema_id !== null && CENEVAL_TEMA_IDS.has(Number(q.tema_id))
  );

  // 5. Filtrar las preguntas base a los temas de las áreas 1 y 2 permitidos por el grado.
  //    Los conocimientos específicos (Área 3) se cargan aparte desde especialidades_temas.
  //    IMPORTANTE: NUNCA se hace respaldo al banco completo cuando el usuario tiene un
  //    jerarquía/perfil detectado. Hacerlo filtraba materias y temas ajenos (de otras
  //    especialidades, de la bóveda o de otros usuarios) en el dropdown del Modo Guardia.
  if (jerarquiaKey && temas.length > 0) {
    const temasPermitidos = temas.filter(t => Number(t.area_id) === 1 || Number(t.area_id) === 2);
    const temasDelGrado = temasPermitidos.filter(temaCoincideConGrado);
    // Solo se conservan las preguntas de los temas permitidos por el grado.
    // Si no hay temas del grado (o el filtrado queda vacío), se mantiene vacío el pool
    // de conocimientos base en lugar de volcar el banco completo.
    const allowedIds = new Set(temasDelGrado.map(t => String(t.id)));
    filtradas = preguntasBase.filter(q =>
      q.tema_id !== undefined && q.tema_id !== null && allowedIds.has(String(q.tema_id))
    );
  }

  return {
    questions: filtradas,
    jerarquiaKey,
    jerarquiaNombre: jerarquiaKey ? GUARDIA_JERARQUIA_NOMBRE[jerarquiaKey] : null,
    especificosCount: 0,
    filtradoPorPerfil: !!jerarquiaKey && temas.length > 0
  };
}
